/**
 * FT-446: Dynamic category-filter options for the vessel Planned-Maintenance tab.
 *
 * BUG (pre-FT-446): the vessel category filter rendered a HARDCODED list of the
 * nine standard maintenance categories. Consequences:
 *   - a NEW/custom category (free-text, e.g. "hydraulics") a user created never
 *     appeared, so its jobs could not be filtered to;
 *   - a standard category with ZERO jobs still appeared (dead option).
 *
 * FIX: derive the option set from the DISTINCT categories actually present on the
 * live jobs list. Only categories with >=1 job appear; a custom category appears
 * with a readable label; the 'all' option is always first.
 *
 * Standard-category labels are kept IDENTICAL to the pre-FT-446 inline dropdown
 * ("Life Saving", "Fire Fighting", …) so this is a strict superset with no visible
 * wording change for recognised categories. Custom categories are title-cased.
 *
 * The match in PlannedMaintenanceTab is exact (`job.category === categoryFilter`),
 * so each option's `value` MUST be the raw stored category string.
 */

// Labels for the recognised/standard maintenance categories. Kept verbatim from
// the previous inline <Dropdown> option arrays so standard categories read
// exactly as they did before FT-446.
export const STANDARD_CATEGORY_LABELS = {
  surveys: 'Surveys',
  lsa: 'Life Saving',
  ffe: 'Fire Fighting',
  machinery: 'Machinery',
  electrical: 'Electrical',
  navigation: 'Navigation',
  radio: 'Radio',
  medical: 'Medical',
  special_ops: 'Special Ops',
};

/**
 * Title-case a free-text custom category for display.
 * 'hydraulic system' -> 'Hydraulic System'. Underscores/hyphens become spaces
 * so a stored 'deck_gear' reads 'Deck Gear'.
 */
export const titleCaseCategory = (raw) => {
  const s = String(raw || '').trim().replace(/[_-]+/g, ' ');
  if (!s) return '';
  return s
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
};

/**
 * Resolve a stored category value to its display label.
 * Recognised standard code -> canonical short label; anything else -> title-cased
 * verbatim value (custom categories).
 */
export const getCategoryFilterLabel = (value) => {
  const lower = String(value || '').trim().toLowerCase();
  return STANDARD_CATEGORY_LABELS[lower] || titleCaseCategory(value);
};

/**
 * Build the ordered category-filter option list from a jobs array.
 *
 * @param {Array<{category?: string}>} jobs - the live (active) jobs list.
 * @returns {Array<{value: string, label: string}>} - always starts with
 *          `{ value: 'all', label: 'All Categories' }`, then one option per
 *          DISTINCT non-empty category present on the jobs, ordered by label.
 *
 * Notes:
 *  - Jobs with a missing/blank category are ignored (they contribute no option
 *    but still appear under 'All').
 *  - De-duplication is by the raw stored string (so 'ffe' collapses to one
 *    option even across many jobs).
 *  - Standard codes sort by their friendly label; custom categories sort by
 *    their title-cased label — a single alphabetical pass over labels.
 */
export const buildMaintenanceCategoryOptions = (jobs = []) => {
  const seen = new Set();
  const options = [];

  (Array.isArray(jobs) ? jobs : []).forEach((job) => {
    const raw = job && job.category != null ? String(job.category).trim() : '';
    if (!raw) return;
    if (seen.has(raw)) return;
    seen.add(raw);
    options.push({ value: raw, label: getCategoryFilterLabel(raw) });
  });

  options.sort((a, b) => a.label.localeCompare(b.label));

  return [{ value: 'all', label: 'All Categories' }, ...options];
};

export default buildMaintenanceCategoryOptions;
